import {GetState, SetState} from "zustand";
import {CustomerServiceDashboardState, CustomerServiceDashboardStore} from "./useCustomerServiceDashboardStore";
import {
    ContractDisplays,
    CustomerServiceRepresentative,
    CustomerServiceRepresentativeAssignedAccounts,
    DisplayPagingResponse,
    MonitoringLog,
    MonitoringStatusItem
} from "../../../Controllers/CustomerServiceDashboard/CustomerServiceDashboard";
import Account = Mri.Accounts.Account;
import Contract = Mri.Contracts.Contract;
import {ActiveDisplay} from "../../Displays/ActiveDisplay";
import {
    ActionType,
    createMonitoringLogEvent, getAllContracts,
    getAllMonitoringLogs,
    getCustomerServiceAssignmentsAsync,
    getCustomerServiceRepresentativesAsync, getDisplaysBySearchModel,
    getMonitoringActiveAccounts,
    getMonitoringLogsByAccountKey,
    getMonitoringStatusAccountList, loadAccountContracts
} from "../context";
import {useStore} from "../../hooks/use-store";
import {toArray, uuidv4generate} from "Mri.Web.Common";
import {Draft, produce} from "immer";


export interface CustomerServiceDashboardActions {
    fetchMonitoringLogs: () => void;
    selectMonitoringLog: (log: MonitoringLog) => void;
    fetchAllAccounts: () => void;
    fetchMonitoringLogsByAccountKey: (accountNumber: Mri.Accounts.Account['accountNumber']) => void;
    createMonitoringLogEvent: (monitoringLog: MonitoringLog) => void;
    updateMonitoringLogEvent: (monitoringLog: MonitoringLog) => void;
    deleteMonitoringLogEvent: (monitoringLog: MonitoringLog) => void;
    fetchAllContracts: () => void;
    fetchAccountContracts: (accountNumber: Mri.Accounts.Account["accountNumber"]) => void;
    selectContract: (contract: Contract) => void;
    clearSelectedContract: () => void;
    fetchDisplaysByContract: (serviceCO: Contract["serviceCO"]) => void;
    selectDisplay: (display: ActiveDisplay) => void;
    clearSelectedDisplay: () => void;
    fetchMonitoringStatusAccountList: () => void;
    fetchCustomerServiceReps: () => void;
    fetchCustomerServiceRepAssignedAccounts: () => void;
}

type CustomerServiceDashboardActionsSet = (nextStateOrUpdater: (CustomerServiceDashboardStore | Partial<CustomerServiceDashboardStore> | ((state: Draft<CustomerServiceDashboardStore>) => void)), shouldReplace?: (boolean | undefined)) => void

export const createCustomerServiceDashboardActions = (set: CustomerServiceDashboardActionsSet, get: () => CustomerServiceDashboardStore): CustomerServiceDashboardActions => ({
    fetchMonitoringLogs: async () => {
        set(produce((state) => {
            state.loading.monitoringLogs = true
        }));
        const {signal} = new AbortController();
        try {
            const response = await getAllMonitoringLogs({signal});
            set(produce((state) => {
                    state.monitoringLogs = response;
                    state.loading.monitoringLogs = false;
                }
            ));
        } catch (error) {
            set(produce((state) => {
                state.error = error;
                state.loading.monitoringLogs = false;
            }));
        }
    },
    selectMonitoringLog: (log) => set(produce((state) => {
        state.selectedLog = log;
    })),
    fetchAllAccounts: async () => {
        set(produce((state) => {
            state.loading.accounts = true;
        }));
        const {signal} = new AbortController();
        try {
            const response = await getMonitoringActiveAccounts({signal});
            set(produce((state) => {
                state.accounts = response;
                state.loading.accounts = false;
            }));
        } catch (error) {
            set(produce((state) => {
                state.error = error;
                state.loading.accounts = false;
            }));
        }
    },
    fetchMonitoringLogsByAccountKey: async (accountNumber) => {
        set(produce((state) => {
            state.loading.monitoringLogsByAccountKey = true;
        }));
        const {signal} = new AbortController();
        try {
            const response = await getMonitoringLogsByAccountKey(parseInt(accountNumber), {signal});
            set(produce((state) => {
                state.monitoringLogs[accountNumber] = response;
                state.loading.monitoringLogsByAccountKey = false;
            }));
        } catch (error) {
            set(produce((state) => {
                state.error = error;
                state.loading.monitoringLogsByAccountKey = false;
            }));
        }
    },
    createMonitoringLogEvent: async (monitoringLog) => {
        set(produce((state) => {
            state.loading.createMonitoringLog = true;
        }));
        const {signal} = new AbortController();
        try {
            const response = await createMonitoringLogEvent(monitoringLog, {signal});
            set(produce((state) => {
                state.monitoringLog = response;
                state.loading.createMonitoringLog = false;
            }));
        } catch (error) {
            set(produce((state) => {
                state.error = error;
                state.loading.createMonitoringLog = false;
            }));
        }
    },
    updateMonitoringLogEvent: (monitoringLog: MonitoringLog) => set(produce((state) => {
        state.loading.updateMonitoringLog = false;
        state.monitoringLog = monitoringLog;
    })),
    deleteMonitoringLogEvent: (monitoringLog) => set(produce((state) => {
        state.loading.deleteMonitoringLog = true;
        state.error = null;
    })),
    fetchAllContracts: async () => {
        set(produce((state) => {
            state.loading.contracts = true;
            state.error = null;
        }));
        try {
            const {signal} = new AbortController();
            const contracts = await getAllContracts({signal})
            set(produce((state) => {
                state.loading.contracts = false;
                state.contracts.contracts = contracts;
            }));
        } catch (error) {
            set(produce((state) => {
                state.loading.contracts = false;
                state.error = error;
            }));
        }
    },
    fetchAccountContracts: async (accountNumber) => {
        set(produce((state) => {
            state.loading.accountContracts = true;
            state.error = null;
        }));
        const {signal} = new AbortController();
        try {
            const response = await loadAccountContracts(accountNumber, {signal}) ?? [];
            set(produce((state) => {
                state.loading.accountContracts = false;
                state.accountContracts[accountNumber] = response;
            }));
        } catch (error) {
            set(produce((state) => {
                state.loading.accountContracts = false;
                state.error = error;
            }));
        }
    },
    selectContract: (contract) => set(produce((state) => {
        state.selectedContract = {...contract};
    })),
    clearSelectedContract: () => set(produce((state) => {
        state.selectedContract = null;
    })),
    fetchDisplaysByContract: async (serviceCO) => {
        set(produce((state) => {
            state.loading.displaysByContract = true;
            state.error = null;
        }));
        const {signal} = new AbortController();
        const {selectedContract} = get();
        const model: Mri.Web.Internal.Controllers.Displays.SearchModel = {
            displayFilter: {
                contracts: selectedContract ? [selectedContract] : [],
            },
            limit: 40,
            offset: 0,
            orderBy: '',
            searchGuid: uuidv4generate(),
            status: true,
            software: true,
            telemetry: false,
            contract: true,
            where: '',
            favorites: false,
            aggregates: false,
            config: true,
            startDate: new Date(),
            endDate: new Date(),
        };
        try {
            const response = await getDisplaysBySearchModel(model, {signal});
            if (!response) throw new Error("No response");
            const displays = response.result as ActiveDisplay[];
            set(produce((state) => {
                state.loading.displaysByContract = false;
                state.displaysByContract[serviceCO] = {...response, displays};
            }));
        } catch (error) {
            set(produce((state) => {
                state.loading.displaysByContract = false;
                state.error = error;
            }));
        }
    },
    selectDisplay: (display) => set(produce((state) => {
        state.selectedDisplay = display;
    })),
    clearSelectedDisplay: () => set(produce((state) => {
        state.selectedDisplay = null;
    })),
    fetchMonitoringStatusAccountList: async () => {
        set(produce((state) => {
            state.loading.monitoringStatusAccountList = true;
            state.error = null;
        }));
        const abortController = new AbortController();
        const {signal} = abortController;
        try {
            const response = await getMonitoringStatusAccountList({signal});
            set(produce((state) => {
                state.loading.monitoringStatusAccountList = false;
                state.monitoringStatusAccountList = response?.length ? [...response] : [];
            }));
        } catch (error) {
            set(produce((state) => {
                state.loading.monitoringStatusAccountList = false;
                state.error = error;
            }));
        }
    },
    fetchCustomerServiceReps: async () => {
        set(produce((state) => {
            state.loading.customerServiceReps = true;
            state.error = null;
        }));
        const {signal} = new AbortController();
        try {
            const response = await getCustomerServiceRepresentativesAsync({signal}) as string[];
            set(produce((state) => {
                state.loading.customerServiceReps = false;
                state.customerServiceReps = [...response];
            }));
        } catch (error) {
            set(produce((state) => {
                state.loading.customerServiceReps = false;
                state.error = error;
            }));
        }
    },
    fetchCustomerServiceRepAssignedAccounts: async () => {
        set(produce((state) => {
            state.loading.repAssignedAccounts = true;
            state.error = null;
        }));
        const {accountStore} = useStore();
        const {user} = accountStore;
        const {signal} = new AbortController();
        try {
            if (!user) throw new Error("User not found");
            const response = await getCustomerServiceAssignmentsAsync(user.userName, {signal}) ?? [];
            set(produce((state) => {
                state.loading.repAssignedAccounts = false;
                state.repAssignedAccounts[user.userName] = response ?? [];
                state.accountsVisibleToUser = state.accounts.filter(
                    (account: Account) => response.indexOf(account.accountNumber) === -1
                );
            }));
        } catch (error) {
            set(produce((state) => {
                state.loading.repAssignedAccounts = false;
                state.error = error;
            }));
        }
    }
});
export default createCustomerServiceDashboardActions;